import type { CustomError } from 'utils/customError'

/**
 * Get the error code from the custom error object.
 * It uses a resolver function to extract the code
 *
 * @param errorObj - The error object
 * @param resolverFn - The function that takes a string of error message and returns the error code
 * @returns The error code
 */
export function getErrorCode(
  errorObj: CustomError,
  resolverFn = errorCodeResolver
): string | undefined {
  const errorMessage = errorObj?.originalError?.response?.data?.error?.message

  if (!errorMessage) return undefined

  return resolverFn(errorMessage)
}

/**
 * Get the error code from the error message which is separated by a delimiter, i.e. " - ".
 * You can use a custom delimiter via delimiter argemument.
 *
 * @example
 * // returns "4001"
 * errorCodeResolver("4001 - Invalid state argument")
 * errorCodeResolver("4001: Invalid state argument", ": ")
 *
 * // returns undefined
 * errorCodeResolver("Invalid state argument")
 *
 * @param errorMessage- A string of error message.
 * @param delimiter - A delimiter of error code and the message. The default value is " - ".
 * @returns The error code
 */
export function errorCodeResolver(
  errorMessage: string,
  delimiter: string = ' - '
): string | undefined {
  const splittedErrorMessage = errorMessage.split(delimiter)
  const hasErrorCode = splittedErrorMessage.length > 1

  if (!hasErrorCode) return undefined

  return splittedErrorMessage[0]
}

/**
 * Generate a formatted error message with error code
 * @example
 * // returns "Gagal kumpulkan dokumen. Coba lagi. (Error code: 4001)"
 * generateErrorMessage("Gagal kumpulkan dokumen. Coba lagi", "4001")
 *
 * @param message - A string of error message
 * @param errorCode - A string of error code
 * @returns A formatted error message
 */
export function generateErrorMessage(
  message: string,
  errorCode?: string
): string {
  const prefixedErrorCode = errorCode ? `. (Error code: ${errorCode})` : ''

  return message + prefixedErrorCode
}
